import { useState, useEffect, useMemo, useCallback } from 'react';
import { Palette, CheckCircle2, Trash2, List, CalendarDays, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { useStore } from '../store/useStore';
import { formatCurrency } from '../lib/utils';
import { effHours, TARGET_RATE, type SalesEntry } from './Sales';

const ET_MONTHS_SHORT = ['jaan', 'veebr', 'märts', 'apr', 'mai', 'juuni', 'juuli', 'aug', 'sept', 'okt', 'nov', 'dets'];
const ET_MONTHS_FULL = ['Jaanuar', 'Veebruar', 'Märts', 'Aprill', 'Mai', 'Juuni', 'Juuli', 'August', 'September', 'Oktoober', 'November', 'Detsember'];

const round1 = (n: number) => Math.round(n * 10) / 10;
const dstr = (iso: string | null) => (iso ? iso.split('T')[0] : '');

interface DesignTaskApi {
  id: string;
  title: string;
  designer: string;
  hours: number;
  doneHours: number | null;
  estimate: string;
  note: string;
  startDate: string | null;
  endDate: string | null;
}

/** One board row, whichever table it lives in. */
interface DesignRow {
  id: string;
  source: 'sales' | 'task';
  title: string;
  subtitle: string; // month + € for sales rows, "sisemine töö" for tasks
  designer: string;
  designAmount: number | null; // sales rows only — drives the auto hours
  hoursManual: number | null;
  doneHours: number | null;
  estimate: string;
  note: string;
  startDate: string | null;
  endDate: string | null;
}

// Stable chip colour per designer name.
const DESIGNER_COLORS = [
  'bg-blue-100 text-blue-700 border-blue-200',
  'bg-purple-100 text-purple-700 border-purple-200',
  'bg-green-100 text-green-700 border-green-200',
  'bg-amber-100 text-amber-700 border-amber-200',
  'bg-pink-100 text-pink-700 border-pink-200',
  'bg-teal-100 text-teal-700 border-teal-200',
];
function designerColor(name: string): string {
  if (!name) return 'bg-gray-100 text-gray-500 border-gray-200';
  let h = 0;
  for (const c of name) h = (h + c.charCodeAt(0)) % 997;
  return DESIGNER_COLORS[h % DESIGNER_COLORS.length];
}

// ── Monthly capacity ─────────────────────────────────────────────────────────

/** In-house design capacity per month (Johan + Aulis). */
const IN_HOUSE_CAPACITY = 224;
const IN_HOUSE_DESIGNERS = ['johan', 'aulis'];

type Bucket = 'inhouse' | 'freelancer' | 'none';

function bucketOf(designer: string): Bucket {
  const d = designer.trim().toLowerCase();
  if (!d) return 'none';
  return IN_HOUSE_DESIGNERS.some((n) => d.includes(n)) ? 'inhouse' : 'freelancer';
}

/** Spread a job's hours across the months its start–end range covers,
 *  proportionally to calendar days per month. */
function spreadHours(startIso: string, endIso: string, hoursTotal: number): Map<string, number> {
  const out = new Map<string, number>();
  const start = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);
  if (!(start <= end) || !hoursTotal) {
    out.set(startIso.slice(0, 7), hoursTotal);
    return out;
  }
  const totalDays = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  const cursor = new Date(start);
  const perMonthDays = new Map<string, number>();
  while (cursor <= end) {
    const k = `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, '0')}`;
    perMonthDays.set(k, (perMonthDays.get(k) ?? 0) + 1);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  for (const [k, d] of perMonthDays) out.set(k, (hoursTotal * d) / totalDays);
  return out;
}

function MonthlyLoad({ jobs }: { jobs: { row: DesignRow; algmaht: number; tehtud: number | null }[] }) {
  const months = useMemo(() => {
    const now = new Date();
    const out: string[] = [];
    for (let i = -2; i <= 9; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return out;
  }, []);

  const { perMonth, unscheduled } = useMemo(() => {
    const acc = new Map<string, { inhouse: number; freelancer: number; none: number; done: number }>();
    for (const m of months) acc.set(m, { inhouse: 0, freelancer: 0, none: 0, done: 0 });
    let unsched = 0;
    for (const { row, algmaht, tehtud } of jobs) {
      if (!row.startDate) { unsched++; continue; }
      const start = dstr(row.startDate);
      const end = dstr(row.endDate) || start;
      const bucket = bucketOf(row.designer ?? '');
      for (const [k, h] of spreadHours(start, end, algmaht)) {
        const slot = acc.get(k);
        if (slot) slot[bucket] += h;
      }
      // Done hours spread over the same window, so each month shows how much
      // of its load is already delivered.
      if (tehtud) {
        for (const [k, h] of spreadHours(start, end, tehtud)) {
          const slot = acc.get(k);
          if (slot) slot.done += h;
        }
      }
    }
    return { perMonth: acc, unscheduled: unsched };
  }, [jobs, months]);

  const H = 140;
  const BAR_W = 46;
  const GAP = 18;
  const W = months.length * (BAR_W + GAP);
  const maxVal = Math.max(
    ...months.map((m) => { const v = perMonth.get(m)!; return v.inhouse + v.freelancer + v.none; }),
    IN_HOUSE_CAPACITY * 1.15,
  );
  const capY = 14 + H - (IN_HOUSE_CAPACITY / maxVal) * H;
  const nowKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Kuu koormus</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Tunnid jaotatud algus–lõpp vahemiku järgi · in-house maht {IN_HOUSE_CAPACITY} h/kuu (Johan + Aulis)
          </p>
        </div>
        <div className="flex gap-4 flex-wrap text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-400" /> In-house</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-purple-400" /> Freelancer</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-gray-200" /> Määramata</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-4 border-t-2 border-dashed border-red-400" /> {IN_HOUSE_CAPACITY} h</span>
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <svg width={Math.max(W, 400)} height={H + 48} className="block">
          <line x1={0} y1={capY} x2={W} y2={capY} stroke="#f87171" strokeWidth={1.5} strokeDasharray="5 4" />
          {months.map((m, i) => {
            const x = i * (BAR_W + GAP);
            const v = perMonth.get(m)!;
            const total = v.inhouse + v.freelancer + v.none;
            const over = v.inhouse > IN_HOUSE_CAPACITY;
            const segs = [
              { val: v.inhouse, color: over ? '#ef4444' : '#60a5fa' },
              { val: v.freelancer, color: '#c084fc' },
              { val: v.none, color: '#e5e7eb' },
            ];
            let y = 14 + H;
            const [yy, mm] = m.split('-');
            return (
              <g key={m}>
                {segs.map(({ val, color }, si) => {
                  if (!val) return null;
                  const h = (val / maxVal) * H;
                  y -= h;
                  return <rect key={si} x={x} y={y} width={BAR_W} height={h} fill={color} rx={si === segs.filter(sg => sg.val > 0).length - 1 ? 3 : 0} opacity={0.9} />;
                })}
                {total > 0 && (
                  <text x={x + BAR_W / 2} y={y - 4} textAnchor="middle" fontSize={9} fontWeight={600} fill={over ? '#dc2626' : '#6b7280'}>
                    {Math.round(total)} h
                  </text>
                )}
                <text x={x + BAR_W / 2} y={14 + H + 14} textAnchor="middle" fontSize={10} fontWeight={m === nowKey ? 700 : 400} fill={m === nowKey ? '#4f46e5' : '#9ca3af'}>
                  {ET_MONTHS_SHORT[Number(mm) - 1]}
                </text>
                <text x={x + BAR_W / 2} y={14 + H + 26} textAnchor="middle" fontSize={9} fill="#d1d5db">{yy.slice(2)}</text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Numbers per month */}
      <div className="overflow-x-auto scrollbar-thin mt-2">
        <table className="text-xs min-w-full">
          <thead>
            <tr>
              <th className="text-left pr-3 py-1 font-medium text-gray-400">h / kuu</th>
              {months.map((m) => (
                <th key={m} className={`px-2 py-1 text-right font-medium ${m === nowKey ? 'text-brand-600' : 'text-gray-400'}`}>
                  {ET_MONTHS_SHORT[Number(m.split('-')[1]) - 1]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="pr-3 py-0.5 text-gray-500">In-house</td>
              {months.map((m) => {
                const v = perMonth.get(m)!.inhouse;
                return <td key={m} className={`px-2 py-0.5 text-right ${v > IN_HOUSE_CAPACITY ? 'text-red-500 font-semibold' : 'text-gray-700'}`}>{v ? Math.round(v) : '—'}</td>;
              })}
            </tr>
            <tr>
              <td className="pr-3 py-0.5 text-gray-500">Freelancer</td>
              {months.map((m) => {
                const v = perMonth.get(m)!.freelancer;
                return <td key={m} className="px-2 py-0.5 text-right text-gray-700">{v ? Math.round(v) : '—'}</td>;
              })}
            </tr>
            <tr>
              <td className="pr-3 py-0.5 text-gray-500">Määramata</td>
              {months.map((m) => {
                const v = perMonth.get(m)!.none;
                return <td key={m} className="px-2 py-0.5 text-right text-gray-400">{v ? Math.round(v) : '—'}</td>;
              })}
            </tr>
            <tr className="border-t border-gray-100">
              <td className="pr-3 py-0.5 font-semibold text-gray-700">Kokku</td>
              {months.map((m) => {
                const v = perMonth.get(m)!;
                const t = v.inhouse + v.freelancer + v.none;
                return <td key={m} className="px-2 py-0.5 text-right font-semibold text-gray-900">{t ? Math.round(t) : '—'}</td>;
              })}
            </tr>
            <tr>
              <td className="pr-3 py-0.5 text-gray-500">Tehtud</td>
              {months.map((m) => {
                const d = perMonth.get(m)!.done;
                return <td key={m} className="px-2 py-0.5 text-right text-green-600">{d ? Math.round(d) : '—'}</td>;
              })}
            </tr>
            <tr>
              <td className="pr-3 py-0.5 text-gray-500">Valmis %</td>
              {months.map((m) => {
                const v = perMonth.get(m)!;
                const t = v.inhouse + v.freelancer + v.none;
                if (!t) return <td key={m} className="px-2 py-0.5 text-right text-gray-300">—</td>;
                const pct = Math.round((v.done / t) * 100);
                return <td key={m} className={`px-2 py-0.5 text-right font-medium ${pct >= 100 ? 'text-green-600' : pct > 0 ? 'text-gray-600' : 'text-gray-300'}`}>{pct}%</td>;
              })}
            </tr>
            <tr>
              <td className="pr-3 py-0.5 text-gray-500">Vaba in-house</td>
              {months.map((m) => {
                const free = IN_HOUSE_CAPACITY - perMonth.get(m)!.inhouse;
                return <td key={m} className={`px-2 py-0.5 text-right ${free < 0 ? 'text-red-500 font-semibold' : 'text-green-600'}`}>{Math.round(free)}</td>;
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {unscheduled > 0 && (
        <p className="text-[11px] text-gray-400 mt-2">{unscheduled} rida ilma alguskuupäevata ei kajastu koormuses.</p>
      )}
    </div>
  );
}

// ── Small editable cells ─────────────────────────────────────────────────────

function NumCell({
  value,
  autoValue,
  onSave,
}: {
  value: number | null;
  autoValue?: number | null;
  onSave: (v: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState('');
  const shown = value ?? autoValue ?? null;

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        min={0}
        step={0.5}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          setEditing(false);
          const v = local.trim() === '' ? null : Number(local);
          if (v !== value) onSave(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="w-20 px-1.5 py-0.5 text-sm text-right border border-brand-400 rounded focus:outline-none"
      />
    );
  }
  return (
    <button
      onClick={() => { setLocal(shown != null ? String(round1(shown)) : ''); setEditing(true); }}
      className={`w-full px-1.5 py-0.5 text-sm text-right rounded hover:bg-brand-50 whitespace-nowrap ${
        shown == null ? 'text-gray-300' : value == null ? 'text-gray-400' : 'text-gray-700'
      }`}
      title={value == null && autoValue != null ? `Automaatne: disaini summa / ${TARGET_RATE}. Vajuta, et muuta` : 'Muuda'}
    >
      {shown != null ? `${value == null && autoValue != null ? '≈ ' : ''}${round1(shown)}` : '—'}
    </button>
  );
}

/** Done-% cell: shown and edited as percent of algmaht; stored as hours. */
function PctCell({
  pct,
  disabled,
  onSave,
}: {
  pct: number | null;
  disabled?: boolean;
  onSave: (pct: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState('');
  if (disabled) return <span className="block px-1.5 py-0.5 text-sm text-gray-300 text-right">—</span>;
  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        min={0}
        max={300}
        step={5}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          setEditing(false);
          const v = local.trim() === '' ? null : Math.max(0, Number(local));
          if (v !== (pct != null ? Math.round(pct) : null)) onSave(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="w-16 px-1.5 py-0.5 text-sm text-right border border-brand-400 rounded focus:outline-none"
      />
    );
  }
  return (
    <button
      onClick={() => { setLocal(pct != null ? String(Math.round(pct)) : ''); setEditing(true); }}
      className={`w-full px-1.5 py-0.5 text-sm text-right rounded hover:bg-brand-50 whitespace-nowrap ${
        pct == null ? 'text-gray-300' : pct >= 100 ? 'text-green-600 font-medium' : 'text-gray-700'
      }`}
      title="Valmis % algmahust"
    >
      {pct != null ? `${Math.round(pct)}%` : '+ %'}
    </button>
  );
}

function TextCell({
  value,
  onSave,
  placeholder,
  className = '',
}: {
  value: string;
  onSave: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);
  if (editing) {
    return (
      <input
        autoFocus
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { setEditing(false); if (local.trim() !== value) onSave(local.trim()); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setEditing(false); }}
        className={`w-full px-1.5 py-0.5 text-sm border border-brand-400 rounded focus:outline-none ${className}`}
      />
    );
  }
  return (
    <button
      onClick={() => { setLocal(value); setEditing(true); }}
      className={`w-full px-1.5 py-0.5 text-sm text-left rounded hover:bg-brand-50 truncate ${value ? 'text-gray-700' : 'text-gray-300 italic'} ${className}`}
      title={value || 'Muuda'}
    >
      {value || placeholder || '—'}
    </button>
  );
}

function DateCell({ value, onSave }: { value: string | null; onSave: (v: string) => void }) {
  return (
    <input
      type="date"
      value={dstr(value)}
      onChange={(e) => onSave(e.target.value)}
      className={`text-xs border rounded-lg px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 w-[112px] ${
        value ? 'border-gray-200 text-gray-600' : 'border-dashed border-gray-300 text-gray-400'
      }`}
    />
  );
}

// ── Month calendar of scheduled design work ──────────────────────────────────

function DesignCalendar({ rows }: { rows: { row: DesignRow; start: string; end: string }[] }) {
  const now = new Date();
  const [ym, setYm] = useState<[number, number]>([now.getFullYear(), now.getMonth()]);
  const [y, m] = ym;

  const shift = (d: number) => setYm(([yy, mm]) => {
    const t = new Date(yy, mm + d, 1);
    return [t.getFullYear(), t.getMonth()];
  });

  const days = useMemo(() => {
    const first = new Date(y, m, 1);
    const gridStart = new Date(first);
    gridStart.setDate(1 - ((first.getDay() + 6) % 7));
    const out: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      out.push(d);
    }
    return out[35].getMonth() === m ? out : out.slice(0, 35);
  }, [y, m]);

  const key = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const todayKey = key(now);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50">
        <div className="flex items-center gap-1.5">
          <button onClick={() => shift(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold text-gray-800 w-36 text-center">{ET_MONTHS_FULL[m]} {y}</span>
          <button onClick={() => shift(1)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><ChevronRight size={16} /></button>
          <button onClick={() => setYm([now.getFullYear(), now.getMonth()])} className="ml-1 text-xs px-2 py-1 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50">Täna</button>
        </div>
        <span className="text-xs text-gray-400">Ribad algus → lõpp (lõputa töö = üks päev)</span>
      </div>
      <div className="grid grid-cols-7 border-b border-gray-50">
        {['E', 'T', 'K', 'N', 'R', 'L', 'P'].map((d) => (
          <div key={d} className="px-2 py-2 text-center text-xs font-medium text-gray-400">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const k = key(day);
          const inMonth = day.getMonth() === m;
          const jobs = rows.filter((r) => r.start <= k && k <= r.end);
          return (
            <div key={k} className={`min-h-[92px] border-b border-r border-gray-50 p-1.5 ${inMonth ? '' : 'bg-gray-50/40'}`}>
              <div className={`text-xs mb-1 ${
                k === todayKey
                  ? 'w-5 h-5 rounded-full bg-brand-500 text-white flex items-center justify-center font-semibold'
                  : inMonth ? 'text-gray-500' : 'text-gray-300'
              }`}>
                {day.getDate()}
              </div>
              <div className="space-y-0.5">
                {jobs.slice(0, 3).map(({ row }) => (
                  <div
                    key={row.id}
                    className={`px-1.5 py-0.5 rounded border text-[10px] leading-tight truncate ${designerColor(row.designer)}`}
                    title={`${row.title} · ${row.designer || 'disainer määramata'}`}
                  >
                    {row.title}{row.designer ? ` · ${row.designer}` : ''}
                  </div>
                ))}
                {jobs.length > 3 && <span className="text-[10px] text-gray-400 pl-1">+{jobs.length - 3} veel</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function SalesDesign() {
  const currentUser = useStore((s) => s.currentUser);
  const [entries, setEntries] = useState<SalesEntry[]>([]);
  const [tasks, setTasks] = useState<DesignTaskApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hideDone, setHideDone] = useState(false);
  const [designerFilter, setDesignerFilter] = useState('all');
  const [view, setView] = useState<'table' | 'calendar'>('table');
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', designer: '', hours: '', startDate: dstr(new Date().toISOString()), endDate: '', note: '' });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setError('');
      const [sales, dt] = await Promise.all([
        apiClient.get<{ entries: SalesEntry[] }>('/design-board'),
        apiClient.get<{ tasks: DesignTaskApi[] }>('/design-tasks'),
      ]);
      setEntries(sales.entries);
      setTasks(dt.tasks);
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const canUse = currentUser && ['super_admin', 'admin', 'sales', 'sales_pm', 'pm'].includes(currentUser.role);

  // Both sources unified into board rows.
  const allRows: DesignRow[] = useMemo(() => {
    const fromSales: DesignRow[] = entries
      .filter((e) => !e.designHidden && ((e.designAmount != null && Number(e.designAmount) > 0) || e.designHours != null))
      .map((e) => {
        const [y, m] = e.month.split('T')[0].split('-').map(Number);
        return {
          id: e.id,
          source: 'sales' as const,
          title: e.companyName,
          subtitle: `${ET_MONTHS_SHORT[m - 1]} ${String(y).slice(2)} · ${formatCurrency(Number(e.designAmount ?? 0))} disain`,
          designer: e.designer ?? '',
          designAmount: e.designAmount != null ? Number(e.designAmount) : null,
          hoursManual: e.designHours != null ? Number(e.designHours) : null,
          doneHours: e.designDoneHours != null ? Number(e.designDoneHours) : null,
          estimate: e.designEstimate ?? '',
          note: e.designNote ?? '',
          startDate: e.designStartDate,
          endDate: e.designEndDate,
        };
      });
    const fromTasks: DesignRow[] = tasks.map((t) => ({
      id: t.id,
      source: 'task' as const,
      title: t.title,
      subtitle: 'sisemine töö',
      designer: t.designer ?? '',
      designAmount: null,
      hoursManual: t.hours != null ? Number(t.hours) : null,
      doneHours: t.doneHours != null ? Number(t.doneHours) : null,
      estimate: t.estimate ?? '',
      note: t.note ?? '',
      startDate: t.startDate,
      endDate: t.endDate,
    }));
    return [...fromSales, ...fromTasks];
  }, [entries, tasks]);

  const designerNames = useMemo(() => {
    const s = new Set<string>();
    for (const r of allRows) if (r.designer?.trim()) s.add(r.designer.trim());
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [allRows]);

  const rows = useMemo(() => {
    return allRows
      .map((row) => {
        const algmaht = effHours(row.designAmount, row.hoursManual) ?? 0;
        const tehtud = row.doneHours;
        const jaanud = algmaht - (tehtud ?? 0);
        return { row, algmaht, tehtud, jaanud };
      })
      .filter((r) => !hideDone || r.jaanud > 0)
      .filter((r) => designerFilter === 'all'
        || (designerFilter === '' ? !r.row.designer?.trim() : r.row.designer?.trim() === designerFilter))
      .sort((a, b) => (dstr(b.row.startDate) || '0').localeCompare(dstr(a.row.startDate) || '0') || a.row.title.localeCompare(b.row.title));
  }, [allRows, hideDone, designerFilter]);

  const calRows = useMemo(
    () => rows
      .filter(({ row }) => row.startDate)
      .map(({ row }) => ({ row, start: dstr(row.startDate), end: dstr(row.endDate) || dstr(row.startDate) })),
    [rows],
  );

  const totals = useMemo(() => ({
    algmaht: rows.reduce((s, r) => s + r.algmaht, 0),
    tehtud: rows.reduce((s, r) => s + (r.tehtud ?? 0), 0),
    jaanud: rows.reduce((s, r) => s + r.jaanud, 0),
  }), [rows]);

  if (!canUse) {
    return <div className="p-8 text-center text-gray-400">This page is available to admins, salespeople and project managers.</div>;
  }

  // Field-name mapping differs per source.
  const patch = async (row: DesignRow, field: 'designer' | 'hours' | 'done' | 'estimate' | 'note' | 'start' | 'end', value: unknown) => {
    const salesKeys = { designer: 'designer', hours: 'designHours', done: 'designDoneHours', estimate: 'designEstimate', note: 'designNote', start: 'designStartDate', end: 'designEndDate' };
    const taskKeys = { designer: 'designer', hours: 'hours', done: 'doneHours', estimate: 'estimate', note: 'note', start: 'startDate', end: 'endDate' };
    const key = row.source === 'sales' ? salesKeys[field] : taskKeys[field];
    const url = row.source === 'sales' ? `/design-board/${row.id}` : `/design-tasks/${row.id}`;
    try {
      await apiClient.patch(url, { [key]: value });
      await load();
    } catch (e) {
      useStore.getState().addError('updateDesignRow', e);
    }
  };

  const remove = async (row: DesignRow) => {
    try {
      if (row.source === 'sales') {
        await apiClient.patch(`/design-board/${row.id}`, { designHidden: true });
      } else {
        await apiClient.delete(`/design-tasks/${row.id}`);
      }
      await load();
    } catch (e) {
      useStore.getState().addError('removeDesignRow', e);
    }
  };

  const addTask = async () => {
    if (!addForm.title.trim() || !addForm.hours || adding) return;
    setAdding(true);
    try {
      await apiClient.post('/design-tasks', {
        title: addForm.title.trim(),
        designer: addForm.designer.trim(),
        hours: Number(addForm.hours),
        note: addForm.note.trim(),
        startDate: addForm.startDate || null,
        endDate: addForm.endDate || null,
      });
      setAddForm({ title: '', designer: '', hours: '', startDate: dstr(new Date().toISOString()), endDate: '', note: '' });
      setShowAdd(false);
      await load();
    } catch (e) {
      useStore.getState().addError('addDesignTask', e);
    } finally {
      setAdding(false);
    }
  };

  const selectCls = 'text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-700 bg-white';
  const inputCls = 'px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400';
  const grid = 'grid grid-cols-[100px_1fr_112px_112px_72px_72px_72px_90px_1fr_30px] gap-2 items-center';

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Disain</h1>
          <p className="text-gray-500 text-sm mt-1">
            Müügiridade disainitööd + otse lisatud tööd · algmaht = summa / {TARGET_RATE} € või tunnid
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={designerFilter} onChange={(e) => setDesignerFilter(e.target.value)} className={selectCls}>
            <option value="all">Kõik disainerid</option>
            {designerNames.map((n) => <option key={n} value={n}>{n}</option>)}
            <option value="">— määramata</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} className="rounded" />
            Peida valmis
          </label>
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setView('table')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md font-medium ${view === 'table' ? 'bg-white text-brand-600 shadow-sm' : 'text-gray-500'}`}
            >
              <List size={12} /> Tabel
            </button>
            <button
              onClick={() => setView('calendar')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md font-medium ${view === 'calendar' ? 'bg-white text-brand-600 shadow-sm' : 'text-gray-500'}`}
            >
              <CalendarDays size={12} /> Kalender
            </button>
          </div>
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600"
          >
            <Plus size={14} /> Lisa töö
          </button>
        </div>
      </div>

      {/* Quick add — standalone design task */}
      {showAdd && (
        <div className="bg-white rounded-xl border border-brand-200 p-4 mb-5">
          <div className="flex gap-2 flex-wrap items-center">
            <input
              autoFocus
              value={addForm.title}
              onChange={(e) => setAddForm((f) => ({ ...f, title: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') addTask(); }}
              placeholder="Töö nimi *"
              className={`${inputCls} flex-1 min-w-[180px]`}
            />
            <input
              value={addForm.designer}
              onChange={(e) => setAddForm((f) => ({ ...f, designer: e.target.value }))}
              placeholder="Disainer"
              list="designer-names"
              className={`${inputCls} w-32`}
            />
            <datalist id="designer-names">
              {designerNames.map((n) => <option key={n} value={n} />)}
              <option value="Johan" /><option value="Aulis" /><option value="Freelancer" />
            </datalist>
            <input
              type="number"
              min={0}
              step={0.5}
              value={addForm.hours}
              onChange={(e) => setAddForm((f) => ({ ...f, hours: e.target.value }))}
              placeholder="Tunnid *"
              className={`${inputCls} w-24`}
            />
            <input type="date" value={addForm.startDate} onChange={(e) => setAddForm((f) => ({ ...f, startDate: e.target.value }))} className={`${inputCls}`} title="Algus" />
            <input type="date" value={addForm.endDate} onChange={(e) => setAddForm((f) => ({ ...f, endDate: e.target.value }))} className={`${inputCls}`} title="Lõpp" />
            <input
              value={addForm.note}
              onChange={(e) => setAddForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="Kommentaar"
              className={`${inputCls} flex-1 min-w-[140px]`}
            />
            <button
              onClick={addTask}
              disabled={!addForm.title.trim() || !addForm.hours || adding}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 disabled:opacity-50"
            >
              <Plus size={14} /> Lisa
            </button>
          </div>
          <p className="text-[11px] text-gray-300 mt-2">Sisemine/mittemüügi töö — ei mõjuta Müügi numbreid. Müügiga seotud disain tekib tahvlile Müügi lehelt eelarve lisamisel.</p>
        </div>
      )}

      {error && <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 mb-4">{error}</div>}
      {loading && <div className="p-12 text-center text-gray-400 text-sm">Laen…</div>}

      {!loading && <MonthlyLoad jobs={rows} />}

      {!loading && view === 'calendar' && <DesignCalendar rows={calRows} />}

      {!loading && view === 'table' && rows.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Palette size={40} className="mx-auto mb-3 text-gray-200" />
          <p className="text-gray-400 text-sm">Disainitöid pole. Lisa töö ülevalt või lisa müügireale disaini eelarve.</p>
        </div>
      )}

      {!loading && view === 'table' && rows.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[1150px]">
              <div className={`px-5 py-2.5 bg-gray-50 border-b border-gray-100 ${grid}`}>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Disainer</span>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Projekt</span>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Algus</span>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Lõpp</span>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider text-right">Algmaht</span>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider text-right">Tehtud %</span>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider text-right">Jäänud</span>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider text-right">Hinnang</span>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Comment</span>
                <span />
              </div>

              <div className="divide-y divide-gray-50">
                {rows.map(({ row, algmaht, tehtud, jaanud }) => (
                  <div key={`${row.source}-${row.id}`} className={`px-5 py-2 group ${grid} ${jaanud <= 0 && tehtud != null ? 'bg-green-50/40' : ''}`}>
                    <div className="min-w-0">
                      <TextCell value={row.designer} onSave={(v) => patch(row, 'designer', v)} placeholder="disainer…" />
                      {row.designer?.trim() && (
                        <span className={`inline-block mt-0.5 px-1.5 rounded-full border text-[9px] font-medium ${designerColor(row.designer)}`}>{row.designer}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{row.title}</p>
                      <p className={`text-xs ${row.source === 'task' ? 'text-teal-600' : 'text-gray-400'}`}>{row.subtitle}</p>
                    </div>
                    <DateCell value={row.startDate} onSave={(v) => patch(row, 'start', v)} />
                    <DateCell value={row.endDate} onSave={(v) => patch(row, 'end', v)} />
                    <NumCell
                      value={row.hoursManual}
                      autoValue={row.designAmount != null && row.designAmount > 0 ? row.designAmount / TARGET_RATE : null}
                      onSave={(v) => patch(row, 'hours', row.source === 'task' ? (v ?? 0) : v)}
                    />
                    <PctCell
                      pct={algmaht > 0 && tehtud != null ? (tehtud / algmaht) * 100 : null}
                      disabled={algmaht <= 0}
                      onSave={(v) => patch(row, 'done', v == null ? null : round1((algmaht * v) / 100))}
                    />
                    <div className={`px-1.5 py-0.5 text-sm text-right font-semibold whitespace-nowrap ${
                      jaanud < 0 ? 'text-red-500' : jaanud === 0 && tehtud != null ? 'text-green-600' : 'text-gray-900'
                    }`}>
                      {jaanud === 0 && tehtud != null
                        ? <span className="inline-flex items-center gap-1"><CheckCircle2 size={13} /> 0</span>
                        : round1(jaanud)}
                    </div>
                    <TextCell value={row.estimate} onSave={(v) => patch(row, 'estimate', v)} placeholder="—" className="text-right" />
                    <TextCell value={row.note} onSave={(v) => patch(row, 'note', v)} placeholder="kommentaar…" className="text-xs" />
                    <button
                      onClick={() => remove(row)}
                      className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity justify-self-end"
                      title={row.source === 'sales' ? 'Eemalda tahvlilt (müügirida jääb alles)' : 'Kustuta töö'}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>

              <div className={`px-5 py-2.5 bg-gray-50 border-t border-gray-200 ${grid}`}>
                <span />
                <span className="text-xs font-semibold text-gray-500">{rows.length} tööd</span>
                <span />
                <span />
                <span className="text-sm font-bold text-gray-900 text-right">{round1(totals.algmaht)}</span>
                <span className="text-sm font-bold text-gray-900 text-right" title={`${round1(totals.tehtud)} h`}>
                  {totals.algmaht > 0 ? `${Math.round((totals.tehtud / totals.algmaht) * 100)}%` : '—'}
                </span>
                <span className={`text-sm font-bold text-right ${totals.jaanud < 0 ? 'text-red-500' : 'text-gray-900'}`}>{round1(totals.jaanud)}</span>
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-3">
        Müügirea kustutamine eemaldab selle ainult tahvlilt (summad jäävad alles); otse lisatud töö kustub päriselt.
        Rohelise alatooniga "sisemine töö" read ei kajastu Müügi numbrites.
      </p>
    </div>
  );
}
